'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const pathname = usePathname();

  const navItems = [
    { label: 'Home', href: '/' },
    { label: 'Services', href: '/services' },
    { label: 'Our Projects', href: '/our-projects' },
    { label: 'Design Process', href: '/design-process' },
    { label: 'Blogs', href: '/blog' },
    { label: 'About', href: '/about-hayat-interiors' },
    { label: 'Contact', href: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY || currentScrollY < 10) {
        // Scrolling up or at the top
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 80) {
        // Scrolling down and past threshold
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const isActivePath = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname === href;
  }; 

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      <header
        className={`fixed top-0 justify-center  items-center flex left-0 right-0 z-50 transition-all duration-300 bg-card shadow-soft ${
          isVisible ? 'translate-y-0' : '-translate-y-full'
        } ${className}`}
      >
        <div className="w-full max-w-7xl">
          <div className="flex items-center justify-between h-20 px-4 md:px-6 lg:px-0">
            <Link
              href="/"
              className="flex items-center space-x-3 group"
              onClick={(e) => {
                setIsMobileMenuOpen(false);
                if (typeof window !== 'undefined') {
                  const href = '/';
                  if (pathname === href) {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    return;
                  }
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              <div>
                <Image src="/white-logo.png" alt="Hayat Interiors Logo" width={180} height={10} className='w-36 md:w-44 lg:w-48' />
              </div>
            </Link>

            <nav className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => {
                const isContact = item.href === '/contact';
                return (
                  <Link
                    key={item.href}
                    href={isContact ? '/contact#contact-form' : item.href}
                    className={`px-4 py-2 font-body text-sm font-medium rounded-md transition-all duration-200 ${
                      isActivePath(item.href)
                        ? 'text-[#1B5E4F] bg-[#1B5E4F]/10' : 'text-zinc-900 hover:text-[#1B5E4F] hover:bg-[#1B5E4F]/5'
                    }`}
                    onClick={(e) => {
                      if (typeof window !== 'undefined') {
                        if (isContact && pathname === '/contact') {
                          e.preventDefault();
                          const el = document.getElementById('contact-form');
                          if (el) {
                            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          } else {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }
                          return;
                        }
                        if (pathname === item.href) {
                          e.preventDefault();
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                          return;
                        }
                        // Let Next.js navigate (and handle hash if present)
                      }
                    }}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            <div className="hidden lg:flex items-center space-x-4">
              <Link
                href="/contact#contact-form"
                className="px-6 py-2.5 bg-[#1B5E4F] text-primary-foreground font-body font-semibold text-sm rounded-md hover:bg-[#164A3E] transition-all duration-200 shadow-soft hover:shadow-strong"
                onClick={(e) => {
                  if (typeof window !== 'undefined') {
                    if (pathname === '/contact') {
                      e.preventDefault();
                      const el = document.getElementById('contact-form');
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      } else {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                      return;
                    }
                    // Let Next.js navigate and handle the hash
                  }
                }}
              >
                Book Consultation
              </Link>
            </div>

            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-3 -mr-2 text-foreground hover:text-primary transition-colors duration-200 active:scale-95"
              aria-label="Toggle mobile menu"
            >
              <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={28} />
            </button>
          </div>
        </div>
      </header>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-secondary/80 backdrop-blur-sm"
            onClick={toggleMobileMenu}
          />

          <div className="absolute top-20 left-0 right-0 bottom-0 bg-card overflow-y-auto">
            <nav className="px-4 py-6 space-y-1">
              {navItems.map((item) => {
                const isContact = item.href === '/contact';
                return (
                  <Link
                    key={item.href}
                    href={isContact ? '/contact#contact-form' : item.href}
                    onClick={(e) => {
                      toggleMobileMenu();
                      if (typeof window !== 'undefined') {
                        if (isContact && pathname === '/contact') {
                          e.preventDefault();
                          const el = document.getElementById('contact-form');
                          if (el) {
                            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          } else {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }
                          return;
                        }
                        if (pathname === item.href) {
                          e.preventDefault();
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                          return;
                        }
                        // Let Next.js navigate
                      }
                    }}
                    className={`block px-4 py-4 font-body text-base font-medium rounded-lg transition-all duration-200 ${
                      isActivePath(item.href)
                        ? 'text-[#1B5E4F] bg-[#1B5E4F]/10' : 'text-foreground hover:text-[#1B5E4F] hover:bg-[#1B5E4F]/5'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}

              <div className="pt-6 pb-4">
                <Link
                  href="/contact#contact-form"
                  onClick={(e) => {
                    toggleMobileMenu();
                    if (typeof window !== 'undefined') {
                      if (pathname === '/contact') {
                        e.preventDefault();
                        const el = document.getElementById('contact-form');
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        } else {
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                        return;
                      }
                      // Let Next.js navigate and handle the hash
                    }
                  }}
                  className="block w-full px-6 py-4 bg-[#1B5E4F] text-primary-foreground font-body font-semibold text-center text-base rounded-lg hover:bg-[#164A3E] active:bg-[#143D33] transition-all duration-200 shadow-soft"
                >
                  Book Consultation
                </Link>
              </div>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;